import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { AppService } from "src/app/app.service";
import { CcVars } from "src/app/custom-consensus/custom-consensus.model";
import { SetNotificationSettings } from "src/app/store/actions/global-header.actions";
import { AppState } from "src/app/store/state/app.state";
import { AddinReceiverAgent } from "../addin-page-enums";
import {
  CustomConsensusInfo,
  JSONData,
  JSONDataCustom,
} from "../addin-page-models";
import { AddinPageService } from "../addin-page.service";
import { VAIconRegistryService, search } from "@va/core-library/icons";

@Component({
  selector: "app-xl-custom-consensus",
  templateUrl: "./xl-custom-consensus.component.html",
  styleUrls: ["./xl-custom-consensus.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class XlCustomConsensusComponent implements OnInit {
  ccVars: CcVars;
  tickerType = null;
  effectiveDateStart = "";
  CCInfo: any;
  note =
    "Note: Custom Consensus will load once the cell or workbook is refreshed";

  tickers = [];
  noData = null;
  selectedTicker = null;
  companyChange: Subject<string> = new Subject<string>();

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private addinService: AddinPageService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.reset();
    this.iconRegistry.registerIcons([search]);
  }

  reset() {
    this.ccVars = {
      brokerlist: [],
      showOnlyCurrentCc: false,
      sortColumn: "name",
      sortDirection: "ascend",
      dateCustomConsensus: "",
      relativeDays: null,
      customConsensusMode: "byDate",
      customConsensusDateType: "custom",
      customConsensusBrokerType: "reaffirm",
      revisionsAvailableFrom: "",
      ticker: "",
      includeNonCurrentBrokers: false,

      includeNonentitledCurrent: false,
      nonentitledCurrent: [],
      nonentitledCurrentBrokers: [],

      includeNonentitledNoncurrent: false,
      nonentitledNoncurrent: [],
      nonentitledNonCurrentBrokers: [],
    };
  }

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams && queryParams.ticker) {
      this.selectedTicker = queryParams.ticker;
      this.searchTicker(this.selectedTicker);
      this.tickerSelected();
    }
    this.store.dispatch(new SetNotificationSettings({ fetchNewData: false }));
    this.getTickerType();
    this.companyChange
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((keyword) => {
        this.searchTicker(keyword);
      });
    this.resizeForByDate();
  }

  getTickerType() {
    const queryParams = this.route.snapshot.queryParams;
    if (
      queryParams &&
      queryParams.tickertype &&
      queryParams.tickertype.toLowerCase() === "b"
    ) {
      this.tickerType = "B";
    }
  }

  updateWindowSize(dimensions: any): void {
    if (window["WebAppHelper"] && window["WebAppHelper"].ExecuteAction) {
      window["WebAppHelper"].ExecuteAction(
        AddinReceiverAgent.ResizeWizard,
        JSON.stringify(dimensions),
      );
    }
  }

  resizeForByDate() {
    this.updateWindowSize({
      width: 510,
      height: 600,
    });
  }

  resizeForByBroker(c1, c2) {
    let extraHeight = 0;
    if (c1 || c2) {
      extraHeight += 84;
    }
    if (c1 && c2) {
      extraHeight -= 12;
    }
    if (c1) {
      extraHeight += 32;
    }
    if (c2) {
      extraHeight += 32;
    }
    this.updateWindowSize({
      width: 510,
      height: 535 + extraHeight,
    });
  }

  closeBrowserInstance(): void {
    if (window["WebAppHelper"] && window["WebAppHelper"].ExecuteAction) {
      window["WebAppHelper"].ExecuteAction(AddinReceiverAgent.CloseWizard, "");
    }
  }

  onTickerSearchEdit(value: string) {
    this.companyChange.next(value);
  }

  searchTicker(keyword: string): void {
    this.noData = null;
    if (keyword) {
      this.addinService.getTickerList(keyword.trim()).subscribe((resp: any) => {
        if (resp.status === "success" && "Company" in resp.srhData) {
          const companyData = resp.srhData.Company.data;
          if (companyData && companyData.length && this.tickerType === "B") {
            companyData.forEach((company) => (company.sn = company.bt));
          }
          this.tickers = companyData;
        } else if (resp.status === "error") {
          this.noData = resp.message || null;
          this.tickers = [];
        } else {
          this.tickers = [];
        }
      });
    }
  }

  tickerSelected() {
    this.getTickerMetaData().then(() => {
      this.fetchSourceMeta();
    });
  }

  getTickerMetaData() {
    return new Promise((resolve) => {
      this.addinService
        .getOtherDimMeta(this.selectedTicker, this.tickerType)
        .subscribe(
          (resp: any) => {
            if (resp.status === "success") {
              const panelData = resp.panels;
              if (panelData) {
                this.effectiveDateStart = panelData[0].effective_start;
              }
            }
            resolve("");
          },
          () => resolve(""),
        );
    });
  }

  fetchSourceMeta() {
    this.addinService
      .getSourceMeta(this.selectedTicker, this.tickerType)
      .subscribe((resp: any) => {
        if (resp.status === "success") {
          this.CCInfo = resp["panels"][0];
          const brokers = this.CCInfo.values.filter((val) => {
            return val.name.toLowerCase() === "brokers";
          });
          this.CCInfo.brokers = brokers[0].values;
        } else {
          this.CCInfo = null;
        }
        this.setBrokerData();
      });
  }

  setBrokerData() {
    if (this.CCInfo) {
      const ccVars = { ...this.ccVars };
      ccVars.customConsensusMode = "byDate";
      if (this.CCInfo.date_custom_consensus) {
        ccVars.customConsensusMode = "byDate";
      } else if (
        this.CCInfo.brokers.some((broker) => {
          return broker.selected_cc;
        })
      ) {
        ccVars.customConsensusMode = "byBroker";
        this.resizeForByBroker(
          this.CCInfo.non_entitled,
          this.CCInfo.excl_non_entitled,
        );
      }
      if (ccVars.customConsensusMode === "byDate") {
        this.CCInfo.brokers.forEach((broker) => {
          broker.selected_cc = false;
        });
      }
      ccVars.brokerlist = this.CCInfo.brokers;
      ccVars.showOnlyCurrentCc = this.CCInfo.show_only_current_cc;
      ccVars.sortColumn = this.CCInfo.sort_column;
      ccVars.sortDirection = this.CCInfo.sort_order;
      ccVars.customConsensusDateType =
        this.CCInfo.custom_consensus_date_type || "custom";
      ccVars.customConsensusBrokerType =
        this.CCInfo.custom_consensus_broker_type || "reaffirm";
      if (ccVars.customConsensusDateType === "custom") {
        ccVars.dateCustomConsensus = this.CCInfo.date_custom_consensus;
      } else {
        ccVars.dateCustomConsensus = "";
      }
      ccVars.relativeDays = this.CCInfo.relative_days;

      ccVars.revisionsAvailableFrom = this.effectiveDateStart;

      ccVars.ticker = this.selectedTicker;
      ccVars.includeNonCurrentBrokers = !!this.CCInfo.inc_non_current_brokers;
      ccVars.includeNonentitledCurrent = this.CCInfo.selected_cc_not_entitled;

      ccVars.nonentitledCurrent = this.CCInfo.non_entitled;
      ccVars.nonentitledCurrentBrokers = this.CCInfo.non_entitled_brokers;

      ccVars.includeNonentitledNoncurrent =
        this.CCInfo.selected_excl_cc_not_entitled;

      ccVars.nonentitledNoncurrent = this.CCInfo.excl_non_entitled;
      ccVars.nonentitledNonCurrentBrokers =
        this.CCInfo.excl_non_entitled_brokers;

      if (ccVars.sortDirection) {
        // adding end at the end of received 'asc', 'desc' -> 'ascend', 'descend'
        // this is beacause ant lib sort values accepted are 'ascend' | 'descend' | null
        // but our backend stores the short form 'asc', 'desc' in source medata
        ccVars.sortDirection = `${ccVars.sortDirection}end`;
      }

      if (typeof ccVars.nonentitledCurrentBrokers === "string") {
        if (ccVars.nonentitledCurrentBrokers !== "") {
          ccVars.nonentitledCurrentBrokers = [ccVars.nonentitledCurrentBrokers];
        } else {
          ccVars.nonentitledCurrentBrokers = [];
        }
      }
      if (typeof ccVars.nonentitledNonCurrentBrokers === "string") {
        if (ccVars.nonentitledNonCurrentBrokers !== "") {
          ccVars.nonentitledNonCurrentBrokers = [
            ccVars.nonentitledNonCurrentBrokers,
          ];
        } else {
          ccVars.nonentitledNonCurrentBrokers = [];
        }
      }

      this.ccVars = ccVars;
    }
  }

  handleSave(data) {
    const ccInfo = new CustomConsensusInfo();
    ccInfo.company = this.selectedTicker;
    ccInfo.cut = null;
    ccInfo.source = null;
    ccInfo.jsonData = new JSONData();
    const jsonData = new JSONDataCustom();
    let selectedBrokers = data.selectedBrokersByDate;
    if (data) {
      if (data.customConsensusMode === "byDate") {
        if (data.relativeDays) {
          jsonData.relative_days = data.relativeDays;
        }
        if (data.dateCustomConsensus) {
          jsonData.date_custom_consensus = data.dateCustomConsensus;
        }
        jsonData.inc_non_current_brokers = data.includeNonCurrentBrokers
          ? 1
          : 0;
        selectedBrokers = data.selectedBrokersByDate;
      } else {
        selectedBrokers = data.selectedBrokersByGrid;
        jsonData.excl_non_entitled_brokers = data.includeNonentitledNoncurrent
          ? true
          : false;
        jsonData.non_entitled_brokers = data.includeNonentitledCurrent
          ? true
          : false;
      }
    }
    jsonData.selected_cc = selectedBrokers
      .map((id) => {
        const broker = this.CCInfo.brokers.find((res) => res.sourceid === id);
        if (broker) {
          return broker.name;
        }
        return undefined;
      })
      .filter((n) => n);
    jsonData.custom_consensus_date_type =
      data.customConsensusMode === "byDate" && data.relativeDays
        ? "relative"
        : "custom";
    ccInfo.jsonData.Custom = jsonData;
    this.addinService
      .saveCustomConsensus(JSON.stringify(ccInfo), this.tickerType)
      .subscribe(() => {
        this.handleCancel();
      });
    const extraData = {
      ticker: this.selectedTicker,
      addinClientType: "addinweb",
    };
    this.appService.logUserAction(
      {},
      this.selectedTicker,
      "",
      "CC_SAVE",
      extraData,
      "",
      "Excel Add-In",
    );
  }

  handleCancel() {
    this.closeBrowserInstance();
  }

  ccDataChanged(data) {
    if (data.customConsensusMode === "byDate") {
      this.resizeForByDate();
    } else {
      this.resizeForByBroker(
        this.CCInfo.non_entitled,
        this.CCInfo.excl_non_entitled,
      );
    }
  }
}
